import { Spacing } from '@persona-id/design-tokens';
import { memo, useEffect } from 'react';
import { useDragLayer } from 'react-dnd';
import styled, { css } from 'styled-components';

import { moduleOutlineMixin } from 'dashboard/apps/DashboardLayoutEditor/LayoutPreview/layers/shared';
import { ScreenEditorComponentConfigTypes } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-constants';
import {
  COMPONENT_SCHEMA,
  isSupportedComponentConfigLibraryType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/constants';
import {
  isClientDefinedComponentSchema,
  isScreenComponentInComponentLibrary,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import { createMockInquiryPropsForUiComponentEditor } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/utils';
import { PREVIEW_WIDTH } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenPreview/InquiryTemplateScreenPreview';
import { isFlowEditorComponentEditorUseDragLayer } from 'dashboard/dnd/types';
import { BASE } from 'design-system/variables/spacing';
import UiComponentView from 'inquiry/components/UiComponentView';

const ComponentBoundingBox = styled.div<{ showDragPreview: boolean }>`
  ${moduleOutlineMixin}
  position: fixed;
  padding: ${Spacing[2]};
  outline-style: solid;

  --scale: 0.75;
  transform-origin: 0 0;
  transform: translate(calc(10px * var(--scale)), calc(20px * var(--scale)));
  scale: var(--scale);
  z-index: 10;
  pointer-events: none;

  ${({ showDragPreview }) =>
    showDragPreview
      ? css`
          min-width: ${PREVIEW_WIDTH - BASE * 4}px; // width - (screen padding + drag padding)
          min-height: max-content;
        `
      : css`
          width: 112px;
          height: 112px;
        `}
`;

const ComponentPreview = styled.div<{
  showDragPreview: boolean;
}>`
  display: 'block';
  width: 100%;
  height: 100%;
  border-radius: ${({ theme }) => theme.card.borderRadius};
  ${({ showDragPreview, theme }) =>
    !showDragPreview &&
    css`
      background-color: ${theme.color.primary.brand400};
      opacity: 0.3;
    `}
`;

interface ComponentDragPreviewProps {
  setDragStatus: (value: boolean) => void;
}

export const ComponentDragPreview = memo(({ setDragStatus }: ComponentDragPreviewProps) => {
  const collectedProps = useDragLayer<GlobalUseDragLayer>((monitor) => ({
    item: monitor.getItem(),
    currentOffset: monitor.getClientOffset(),
    isDragging: monitor.isDragging(),
    itemType: monitor.getItemType(),
  }));

  useEffect(() => {
    if (isFlowEditorComponentEditorUseDragLayer(collectedProps)) {
      setDragStatus(collectedProps.isDragging);
    }
  }, [collectedProps, setDragStatus]);

  if (!isFlowEditorComponentEditorUseDragLayer(collectedProps)) {
    return null;
  }

  const { item, currentOffset } = collectedProps;

  if (currentOffset == null || !item?.type) {
    return null;
  }

  const moduleStyles = {
    top: `${currentOffset.y}px`,
    left: `${currentOffset.x}px`,
  };

  const screenComponentData = ScreenEditorComponentConfigTypes[item.type];

  const showDragPreview =
    item.name == null &&
    !isClientDefinedComponentSchema(item.type) &&
    isScreenComponentInComponentLibrary(screenComponentData) &&
    !screenComponentData.componentLibrary?.hideUiComponentDragPreview;

  return (
    <ComponentBoundingBox style={moduleStyles} showDragPreview={showDragPreview}>
      <ComponentPreview showDragPreview={showDragPreview}>
        {showDragPreview && isSupportedComponentConfigLibraryType(item.type) && (
          <UiComponentView
            component={{
              ...COMPONENT_SCHEMA[item.type].preview,
              name: 'drag-preview',
            }}
            {...createMockInquiryPropsForUiComponentEditor()}
          />
        )}
      </ComponentPreview>
    </ComponentBoundingBox>
  );
});

ComponentDragPreview.displayName = 'ComponentDragPreview';
